/**
 * Venner · Country notice.
 *
 * Replaces Orbe Geolocation at no cost and with no dependencies:
 *
 *   1. Detects the visitor's physical country via /cdn-cgi/trace — an endpoint on our own
 *      domain (Shopify is served behind Cloudflare), no API key, no rate limit and no data
 *      sent to third parties. If that fails, it falls back to /browsing_context_suggestions.json,
 *      Shopify's native endpoint that the theme's country selector already uses.
 *   2. Checks that country against the rules configured in the theme editor.
 *   3. Suggests (or redirects to) the right domain, keeping the equivalent page and the
 *      campaign parameters so attribution isn't lost.
 *
 * The result is cached in localStorage: a single request per visitor every N hours.
 */

(() => {
  const STORAGE = {
    geo: 'venner:geo:v1',
    pref: 'venner:geo:pref:v1',
    seen: 'venner:geo:seen:v1'
  };

  // Paths where we never interrupt: the customer is already checking out or signing in.
  const BLOCKED_PATHS = /^\/(checkouts?|cart\/|account|tools|apps|challenge|password|\d+\/(checkouts|orders))/;

  const BOT = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|preview|lighthouse|headless|pingdom|gtmetrix|semrush|ahrefs/i;

  const now = () => Date.now();

  function read(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expires && parsed.expires < now()) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    } catch (error) {
      return null;
    }
  }

  function write(key, value, days) {
    try {
      localStorage.setItem(key, JSON.stringify({ value, expires: now() + days * 864e5 }));
    } catch (error) {
      /* Private mode: carry on without persistence. */
    }
  }

  /* ------------------------------------------------------------------ detection */

  async function fromCloudflare(signal) {
    const response = await fetch('/cdn-cgi/trace', { signal, credentials: 'omit' });
    if (!response.ok) return null;
    const match = (await response.text()).match(/^loc=([A-Z]{2})$/m);
    return match ? match[1] : null;
  }

  async function fromShopify(signal) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const response = await fetch(
      `${root}browsing_context_suggestions.json?source=geolocation_recommendation&country[enabled]=true`,
      { signal, headers: { Accept: 'application/json' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || null;
  }

  async function detectCountry(cacheHours) {
    const cached = read(STORAGE.geo);
    if (cached) return cached;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);

    try {
      const country = (await fromCloudflare(controller.signal).catch(() => null)) ||
        (await fromShopify(controller.signal).catch(() => null));
      if (country) write(STORAGE.geo, country, cacheHours / 24);
      return country;
    } finally {
      clearTimeout(timer);
    }
  }

  /* ------------------------------------------------------------------ destination */

  /**
   * Maps the current page to its equivalent on the destination domain.
   *
   * Handles match for most products but not all, so the link carries `vg_h` and `vg_q`:
   * if the page doesn't exist over there, that store's 404 template uses them to send the
   * customer to search results instead of an error page.
   */
  function buildTarget(rule, config, chosenCountry) {
    const base = rule.url.replace(/\/+$/, '');
    const params = new URLSearchParams();
    let path = '/';

    if (config.deepLink) {
      // Strip the language/market prefix: the destination domain has its own.
      const clean = location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
      if (/^\/(products|collections|pages|blogs)\//.test(clean)) path = clean;
    }

    // Keep campaign attribution: without this the sale is recorded as direct.
    const incoming = new URLSearchParams(location.search);
    for (const [key, value] of incoming) {
      if (/^(utm_|gclid|fbclid|ttclid|msclkid|irclickid|epik)/i.test(key)) params.set(key, value);
    }

    params.set('vg_from', config.originCode);
    // The other store uses this to put the customer in the market they picked here.
    if (chosenCountry) params.set('vg_country', chosenCountry);
    if (path !== '/') {
      params.set('vg_h', path.split('/').filter(Boolean).pop());
      if (config.pageTitle) params.set('vg_q', config.pageTitle);
    }

    const query = params.toString();
    return base + path + (query ? `?${query}` : '');
  }

  /**
 * Never suggest the store we're already on. Without this, a visitor from a country with no
 * coverage would see, on the fallback store, a notice inviting them to the fallback store.
 */
function sameOrigin(url) {
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch (error) {
    return false;
  }
}

/* ------------------------------------------------------------------ analytics */

  function track(action, detail) {
    const payload = { event: 'venner_geo', geo_action: action, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('venner:geo', { detail: payload }));
  }

  /* ------------------------------------------------------------------ element */

  class VennerGeo extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-venner-geo-config]').textContent);
      this.dialog = this.querySelector('.venner-geo__backdrop');

      this.querySelectorAll('[data-venner-geo-dismiss]').forEach((node) => {
        node.addEventListener('click', () => this.dismiss());
      });
      this.querySelector('[data-venner-geo-go]').addEventListener('click', (event) => {
        event.preventDefault();
        this.go();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dismiss();
      });

      this.picker = this.querySelector('[data-venner-geo-select]');
      if (this.picker) {
        this.picker.addEventListener('change', () => this.onPick());
        this.buildCombo();
      }

      this.applyIncomingCountry();

      // In the theme editor it doesn't open on its own: it would cover the rest of the page while
      // editing. It only shows when the section is selected, like the base theme's popups.
      if (window.Shopify && Shopify.designMode) {
        const section = this.closest('.shopify-section');

        if (!section) {
          return;
        }

        section.addEventListener('shopify:section:select', () => {
          // `showModal()` throws if the dialog is already open, and the editor can fire several
          // `select` events in a row while jumping between the section's blocks.
          if (this.hasAttribute('open')) {
            return;
          }

          this.rule = this.config.rules[0];
          // Preview as a visitor from the first country in rule 1.
          if (this.rule) this.render(this.rule.countries[0] || this.config.currentCountry).open();
        });

        section.addEventListener('shopify:section:deselect', () => this.close());

        return;
      }

      this.evaluate();
    }

    /**
     * The country notice decides which store the visitor belongs to, so it goes first:
     * other popups (the scratch card) wait for this signal rather than cover it. It is
     * emitted both when the notice is dismissed and when it turns out not to apply.
     */
    settle() {
      if (this.navigating || this.dataset.state === 'done') return;
      this.dataset.state = 'done';
      document.dispatchEvent(new CustomEvent('venner:geo:settled'));
    }

    async evaluate() {
      try {
        await this.decide();
      } finally {
        // If nothing was left on screen, the rest of the page can get on with it.
        if (!this.hasAttribute('open')) this.settle();
      }
    }

    async decide() {
      const search = new URLSearchParams(location.search);

      // Testing tools, to verify without a VPN:
      //   ?vg_reset=1     clears the saved decision and the cached country
      //   ?vg_test=MX     simulates a visitor coming from that country
      if (search.has('vg_reset')) {
        Object.values(STORAGE).forEach((key) => localStorage.removeItem(key));
      }

      const simulated = (search.get('vg_test') || '').toUpperCase().slice(0, 2);
      let preference = null;

      if (!simulated) {
        if (BOT.test(navigator.userAgent) || navigator.webdriver) return;
        if (BLOCKED_PATHS.test(location.pathname)) return;
        if (search.has('vg_from')) return; // Already coming from the other store.

        preference = read(STORAGE.pref);
        if (preference === 'stay') return;
        if (read(STORAGE.seen)) return;
      }

      const country = simulated || (await detectCountry(this.config.cacheHours));
      if (!country) return;

      this.rule = this.resolve(country);
      if (!this.rule) return;

      track('detected', { geo_country: country, geo_target: this.rule.url });

      // Someone who already chose to switch stores shouldn't have to decide again.
      if (this.config.mode === 'auto' || preference === 'go') {
        this.go(true);
        return;
      }

      this.render(country).open();
      track('shown', { geo_country: country });
    }

    /**
     * Arriving from the other store with a country already chosen: apply that market.
     * The guard against the current country keeps the form from resubmitting in a loop.
     */
    applyIncomingCountry() {
      const wanted = (new URLSearchParams(location.search).get('vg_country') || '').toUpperCase();
      if (!wanted || wanted === this.config.currentCountry) return;
      if (!(this.config.localCountries || []).includes(wanted)) return;
      this.submitCountry(wanted);
    }

    /**
     * Records that the visitor already decided. Without this the notice comes back on
     * the next page load, which is exactly what happens when switching markets: the
     * form reloads the page and the notice found nothing in storage.
     */
    remember(decision) {
      write(STORAGE.pref, decision, this.config.rememberDays);
      write(STORAGE.seen, 1, this.config.snoozeDays);
    }

    /** Switches market without leaving the domain, using Shopify's native form. */
    submitCountry(iso) {
      const input = this.querySelector('[data-venner-geo-country-input]');
      if (!input || !input.form) return;
      input.value = iso;
      track('market_change', { geo_country: iso });
      // They chose to stay on this store: record it before reloading.
      this.remember('stay');
      input.form.submit();
    }

    /** The visitor picked a country in the selector. */
    onPick() {
      const option = this.picker.selectedOptions[0];
      if (!option) return;

      const iso = option.value;
      const name = option.textContent.trim();
      const remote = option.dataset.scope === 'remote';
      const go = this.querySelector('[data-venner-geo-go]');

      this.syncCombo();
      this.picked = { iso, name, remote };

      if (!go) return;
      const template = remote ? this.config.pickerGoRemote : this.config.pickerGoLocal;
      if (template) go.textContent = template.replace(/\{country\}/gi, name);
      if (remote) go.href = buildTarget({ url: this.config.remoteUrl }, this.config, iso);
    }

    /** Circular badge with the country's flag. */
    static chip(iso) {
      const chip = document.createElement('span');
      chip.className = 'venner-geo__chip';
      if (/^[A-Z]{2}$/.test(iso)) {
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.src = VennerGeo.flagUrl(iso);
        chip.append(img);
      }
      return chip;
    }

    /**
     * Custom dropdown built from the <select>, which stays as the accessible fallback
     * and as the data source. A native <select> can't render an image per option, and
     * that's exactly what the flags need.
     */
    buildCombo() {
      const combo = document.createElement('div');
      combo.className = 'venner-geo__combo';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'venner-geo__combo-toggle';
      toggle.setAttribute('aria-haspopup', 'listbox');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.append(
        VennerGeo.chip(''),
        Object.assign(document.createElement('span'), { className: 'venner-geo__combo-value' })
      );
      toggle.insertAdjacentHTML(
        'beforeend',
        '<svg viewBox="0 0 12 8" width="12" aria-hidden="true"><path d="M1 1.5 6 6.5l5-5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>'
      );

      const panel = document.createElement('div');
      panel.className = 'venner-geo__combo-panel';
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;

      combo.append(toggle, panel);
      this.picker.before(combo);

      this.combo = combo;
      this.comboToggle = toggle;
      this.comboPanel = panel;

      toggle.addEventListener('click', () => this.toggleCombo());
      toggle.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.toggleCombo(true);
        }
      });
      panel.addEventListener('keydown', (event) => this.onComboKey(event));
      document.addEventListener('click', (event) => {
        if (!combo.contains(event.target)) this.toggleCombo(false);
      });

      this.syncCombo();
    }

    /** Mirrors the <select>'s current selection on the toggle button. */
    syncCombo() {
      if (!this.comboToggle) return;
      const option = this.picker.selectedOptions[0];
      if (!option) return;
      this.comboToggle.firstElementChild.replaceWith(VennerGeo.chip(option.value));
      this.comboToggle.querySelector('.venner-geo__combo-value').textContent = option.textContent.trim();
    }

    /** The flags aren't requested until someone opens the list, not on page load. */
    fillCombo() {
      if (this.comboFilled) return;
      this.comboFilled = true;

      const check =
        '<svg class="venner-geo__check" viewBox="0 0 14 11" width="14" aria-hidden="true"><path d="M1 5.5 5 9.5 13 1.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';

      for (const group of this.picker.children) {
        if (group.tagName === 'OPTGROUP') {
          const title = document.createElement('p');
          title.className = 'venner-geo__combo-group';
          title.textContent = group.label;
          this.comboPanel.append(title);
        }

        for (const option of group.tagName === 'OPTGROUP' ? group.children : [group]) {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'venner-geo__option';
          row.setAttribute('role', 'option');
          row.dataset.value = option.value;
          row.setAttribute('aria-selected', String(option.selected));
          row.append(
            VennerGeo.chip(option.value),
            Object.assign(document.createElement('span'), {
              className: 'venner-geo__option-name',
              textContent: option.textContent.trim()
            })
          );
          row.insertAdjacentHTML('beforeend', check);
          row.addEventListener('click', () => this.choose(option.value));
          this.comboPanel.append(row);
        }
      }
    }

    toggleCombo(force) {
      const open = force === undefined ? this.comboPanel.hidden : force;
      if (open) this.fillCombo();

      this.comboPanel.hidden = !open;
      this.combo.dataset.open = String(open);
      this.comboToggle.setAttribute('aria-expanded', String(open));
      if (!open) return;

      this.placePanel();

      const selected = this.comboPanel.querySelector('[aria-selected="true"]') ||
        this.comboPanel.querySelector('.venner-geo__option');
      if (selected) {
        selected.focus({ preventScroll: true });
        // scrollIntoView would drag the card along and move the panel with it; the panel is
        // the positioned ancestor, so scrolling it is enough.
        this.comboPanel.scrollTop =
          selected.offsetTop - (this.comboPanel.clientHeight - selected.offsetHeight) / 2;
      }
    }

    /**
     * Which way the list opens, and how tall it may get.
     *
     * The card has `overflow-y: auto`, so it clips the panel: anything sticking out above it is
     * out of reach — you can't scroll past scrollTop 0 — and those countries stop being
     * selectable. We measure the room actually left inside the card on each side, the roomier
     * side wins, and the height is capped to that room.
     */
    placePanel() {
      const toggle = this.comboToggle.getBoundingClientRect();
      const card = this.querySelector('.venner-geo__card');
      const box = card ? card.getBoundingClientRect() : null;

      // Clipping happens at the card's padding box edge, not at its content edge.
      const ceiling = Math.max(box ? box.top : 0, 0);
      const floor = Math.min(box ? box.bottom : window.innerHeight, window.innerHeight);

      // The panel's own 6px margin plus a little breathing room off the edge.
      const gap = 14;
      const above = toggle.top - ceiling - gap;
      const below = floor - toggle.bottom - gap;
      const up = above > below;

      this.combo.dataset.drop = up ? 'up' : 'down';
      this.comboPanel.style.setProperty(
        '--vg-panel-max',
        `${Math.round(Math.max(120, Math.min(310, up ? above : below)))}px`
      );
    }

    onComboKey(event) {
      const rows = [...this.comboPanel.querySelectorAll('.venner-geo__option')];
      const index = rows.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        this.toggleCombo(false);
        this.comboToggle.focus();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
        (rows[next] || rows[event.key === 'ArrowDown' ? 0 : rows.length - 1]).focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        (event.key === 'Home' ? rows[0] : rows[rows.length - 1]).focus();
      }
    }

    choose(iso) {
      this.picker.value = iso;
      this.comboPanel.querySelectorAll('.venner-geo__option').forEach((row) => {
        row.setAttribute('aria-selected', String(row.dataset.value === iso));
      });
      this.syncCombo();
      this.toggleCombo(false);
      this.comboToggle.focus();
      this.picker.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Starts the selector on the visitor's country, not the first one in the list. */
    preselect(country) {
      if (!this.picker || !country) return;
      const match = [...this.picker.options].find((option) => option.value === country);
      if (!match) return;
      this.picker.value = country;
      this.syncCombo();
      if (this.comboFilled) {
        this.comboPanel.querySelectorAll('.venner-geo__option').forEach((row) => {
          row.setAttribute('aria-selected', String(row.dataset.value === country));
        });
      }
    }

    /**
     * Where a country belongs, in order of priority:
     *   1. A rule written by hand in the theme editor (override).
     *   2. THIS store's markets: they're already where they should be, nothing to suggest.
     *   3. The OTHER store's markets: suggest switching.
     *   4. Neither: no coverage, offer the fallback store.
     */
    resolve(country) {
      const cfg = this.config;

      const manual = cfg.rules.find((rule) => rule.countries.includes(country));
      if (manual) return manual;

      if ((cfg.localCountries || []).includes(country)) return null;

      if ((cfg.remoteCountries || []).includes(country) && !sameOrigin(cfg.remoteUrl)) {
        return { countries: [country], url: cfg.remoteUrl, label: cfg.remoteLabel, flagCode: country };
      }

      if (!cfg.fallbackUrl || sameOrigin(cfg.fallbackUrl)) return null;
      return {
        countries: [country],
        url: cfg.fallbackUrl,
        label: cfg.fallbackLabel,
        flagCode: country,
        unsupported: true
      };
    }

    render(country) {
      const fill = (value) =>
        value.replace(/\{country\}/gi, this.rule.label).replace(/\{code\}/gi, country);

      if (this.config.showFlag) this.renderFlag(country);

      this.querySelectorAll('[data-venner-geo-text]').forEach((node) => {
        node.textContent = fill(node.dataset.template || node.textContent);
      });

      // No coverage: the message and button change, and there's no point offering a
      // country selector that visitor doesn't belong to.
      if (this.rule.unsupported) {
        const body = this.querySelector('[data-venner-geo-unsupported]');
        if (body) {
          body.hidden = false;
          body.textContent = this.config.unsupportedText.replace(/\{country\}/gi, this.countryName(country));
        }
        this.querySelectorAll('[data-venner-geo-hide-unsupported]').forEach((n) => (n.hidden = true));
        const go = this.querySelector('[data-venner-geo-go]');
        if (go && this.config.unsupportedButton) go.textContent = this.config.unsupportedButton;
      }

      // The selector starts on the visitor's country, not the first one in the list.
      this.preselect(country);

      const link = this.querySelector('[data-venner-geo-go]');
      link.href = buildTarget(this.rule, this.config);
      return this;
    }

    /**
     * The country's flag. In order of preference:
     *   1. The image uploaded to the rule from the theme editor.
     *   2. The official SVG Shopify serves from its CDN for any ISO code.
     *   3. The country emoji, if the SVG fails to load.
     * In all three cases it's cropped to the shape with object-fit: cover.
     */
    renderFlag(country) {
      const wrap = this.querySelector('[data-venner-geo-flag]');
      if (!wrap) return;

      const iso = String(this.rule.flagCode || country).toUpperCase();
      if (!this.rule.flagImage && !/^[A-Z]{2}$/.test(iso)) return;

      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = this.rule.flagImage || VennerGeo.flagUrl(iso);
      img.addEventListener('error', () => this.renderFlagFallback(wrap, iso), { once: true });

      wrap.classList.remove('venner-geo__flag--code');
      wrap.replaceChildren(img);
    }

    /** Country name in the page's language; if that's not possible, the ISO code. */
    countryName(iso) {
      try {
        const names = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
        return names.of(iso) || iso;
      } catch (error) {
        return iso;
      }
    }

    static flagUrl(iso) {
      return `https://cdn.shopify.com/static/images/flags/${iso.toLowerCase()}.svg`;
    }

    /** Windows doesn't draw flag emoji: there we keep the pill with the ISO code. */
    renderFlagFallback(wrap, iso) {
      const slot = document.createElement('span');
      const platform =
        (navigator.userAgentData && navigator.userAgentData.platform) || navigator.userAgent || '';

      if (/Win/i.test(platform)) {
        wrap.classList.add('venner-geo__flag--code');
        slot.textContent = iso;
      } else {
        slot.textContent = String.fromCodePoint(
          0x1f1e6 + iso.charCodeAt(0) - 65,
          0x1f1e6 + iso.charCodeAt(1) - 65
        );
      }
      wrap.replaceChildren(slot);
    }

    open() {
      // The bottom bar must not block navigation; the centered window does.
      if (this.config.layout === 'bar') {
        this.dialog.show();
      } else {
        this.dialog.showModal();
      }
      this.setAttribute('open', '');
      this.dataset.state = 'open';
    }

    close() {
      this.removeAttribute('open');
      setTimeout(() => this.dialog.close(), 300);
      this.settle();
    }

    go(automatic) {
      // If the visitor picked a country served by THIS store, there's no need to switch domains.
      if (this.picked && !this.picked.remote) {
        this.submitCountry(this.picked.iso);
        return;
      }

      const chosen = this.picked && this.picked.remote ? this.picked.iso : null;
      const rule = chosen ? { url: this.config.remoteUrl } : this.rule;
      const url = buildTarget(rule, this.config, chosen);
      track(automatic ? 'auto_redirect' : 'accepted', { geo_target: url });
      this.remember('go');
      location.replace(url);
    }

    dismiss() {
      track('dismissed', {});
      // The customer chose to stay: they may be abroad and still want to ship here.
      this.remember('stay');
      this.close();
    }
  }

  if (!customElements.get('venner-geo')) customElements.define('venner-geo', VennerGeo);
})();
